import { Button, Divider, Grid, Paper } from '@mui/material';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import { Schema } from 'yup';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useParams } from 'react-router-dom';
import { getCustomerById, updateCustomer } from 'src/shared/services/customer';
import { useCallback, useEffect, useState } from 'react';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { t } from 'i18next';
import { toast } from 'react-toastify';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { useGlobals } from 'src/shared/contexts/appContext';
import { CustomerSettings } from '../models/customerSettings';
import { CustomerSettingsCustInformation } from '../components/customerSettingsCustInformation';
import { CustomerSettingsInternalInformation } from '../components/customerSettingsInternalInformation';
import { CustomerSettingsAdditionalServices } from '../components/customerSettingsAdditionalServices';

export const CustomerSettingsDetailPage = () => {
  const { id } = useParams();
  const customerId = id ? parseInt(id, 10) : 0;
  const isMounted = useIsMounted();
  const { currentUser } = useGlobals();

  const [loading, setLoading] = useState<boolean>(false);
  const [fullCustomerSettings, setFullCustomerSettings] = useState<CustomerSettings>();

  const CustomerSettingsSchema: Schema<CustomerSettings> = yup.object().shape({
    member_id: yup.number().required(),
    lastName: yup.string().required(t('common:validationMessages.required')),
    admDate: yup.string().required(),
    coAddress: yup.string().optional(),
    address: yup.string().optional(),
    addressOther: yup.string().optional(),
    postNr: yup.string().optional(),
    postAddress: yup.string().optional(),
    country: yup.string().optional(),
    phone: yup.string().optional(),
    phoneOther: yup.string().optional(),
    email: yup.string().email(t('common:validationMessages.invalidFormat')).optional(),
    custom1: yup.string().optional(),
    custom3: yup.string().optional(),
    custom5: yup.string().optional(),
    custom6: yup.string().optional(),
    custom9: yup.string().optional(),
    custom10: yup.string().optional(),
    newsPaper: yup.string().required(),
    newsPaper2: yup.string().required(),
    newsPaper3: yup.string().required(),
    newsPaper4: yup.string().required(),
    newsPaper5: yup.string().required(),
  });

  const formMethods = useForm<CustomerSettings>({
    resolver: yupResolver(CustomerSettingsSchema),
    mode: 'all',
    defaultValues: {
      member_id: customerId,
      lastName: '',
      admDate: '',
      coAddress: '',
      address: '',
      addressOther: '',
      postNr: '',
      postAddress: '',
      country: '',
      phone: '',
      phoneOther: '',
      email: '',
      custom3: '',
      custom10: '',
      newsPaper: '',
      newsPaper2: '',
      newsPaper4: '',
      newsPaper5: '',
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty },
  } = formMethods;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCustomerById(customerId);
      if (isMounted()) {
        setFullCustomerSettings(res.data);
        reset({
          ...res.data,
          newsPaper: String(res.data.newsPaper),
          newsPaper2: String(res.data.newsPaper2),
          newsPaper4: String(res.data.newsPaper4),
          newsPaper5: String(res.data.newsPaper5),
        });
      }
    } catch (error) {
      if (isMounted()) {
        ConsoleHelper.log(error);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [customerId, isMounted, reset]);

  useEffect(() => {
    if (isMounted() && customerId) {
      fetchData();
    }
  }, [customerId, fetchData, isMounted]);

  const onSubmit = async (data: CustomerSettings) => {
    setLoading(true);

    const dataToPost: CustomerSettings = {
      ...data,
      changedDate: DateHelper.getDateTime(new Date()),
      changedBy: currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : '',
    };
    try {
      await updateCustomer(dataToPost);
      if (isMounted()) {
        toast.success(<>{t('customer:detailPage.updateSuccess')}</>);
        fetchData();
      }
    } catch (e) {
      ConsoleHelper.log(e);
      toast.error(<>{t('customer:detailPage.updateError')}</>);
    } finally {
      setLoading(false);
    }
  };

  return (
    <GridWrapper>
      <Grid item xs={12}>
        <MainContentHeader title={t('customer:header')} />
      </Grid>
      <Grid item xs={12}>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Paper sx={{ p: 3 }}>
              {fullCustomerSettings && (
                <>
                  <CustomerSettingsCustInformation />
                  <Grid item xs={12} mb={2} mt={3}>
                    <Divider />
                  </Grid>

                  <CustomerSettingsInternalInformation />
                  <Grid item xs={12} mb={2} mt={3}>
                    <Divider />
                  </Grid>

                  <CustomerSettingsAdditionalServices />
                  <Grid item xs={12} mb={2} mt={3}>
                    <Divider />
                  </Grid>
                  <Grid display='flex' justifyContent='flex-end' mt={2}>
                    <Button disabled={loading || !isDirty} sx={{ marginLeft: 2 }} type='submit' variant='contained' disableElevation>
                      {t('common:button:save')}
                    </Button>
                  </Grid>
                </>
              )}
            </Paper>
          </form>
        </FormProvider>
      </Grid>
    </GridWrapper>
  );
};
